import { useContext } from "react";

import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import AlertContext, { ALERT_STATUS } from "context/alert/alertContext";
import AuthContext from "context/auth/authContext";
import { API_MESSAGES, ROUTES } from "shared/types/enums";

import { UNKNOWN_ERROR } from "./constants";

const useResponse = () => {
    const authContext = useContext(AuthContext);
    const { setAlert } = useContext(AlertContext);

    const navigate = useNavigate();
    const { t } = useTranslation();

    const showSuccessAlert = (data: any) => {
        const message = data.message || data;

        if (message === API_MESSAGES.GroupWasAdded) {
            setAlert({
                status: ALERT_STATUS.Success,
                title: t(`Flash##${message}`, {
                    group: data.data.name
                })
            });
        } else {
            setAlert({
                status: ALERT_STATUS.Success,
                title: t(`Flash##${message}`, { defaultValue: message })
            });
        }
    };

    const logoutOrNavigateUser = (error: any) => {
        const status = error?.response?.status;

        if (status === 503) {
            navigate(ROUTES.Maintenance, { replace: true });
        }

        if (status === 401 && authContext) {
            authContext.logoutUser?.();
        }

        return status !== 503;
    };

    const getMessageWithResponse = (error: any) => {
        if (error.response.data.errors) {
            return error.response.data.errors[0].errors[0];
        } else {
            return error.response.data.message ?? error.response.statusText;
        }
    };

    const getMessageWithoutResponse = (error: any) => {
        if (error.request) {
            if (error.request.statusText) {
                return error.request.statusText;
            } else {
                return error.message;
            }
        } else {
            return error.message;
        }
    };

    const getCriticalMessage = (errorResponse: any) => {
        const error = errorResponse;
        let message;
        let isAlert = true;

        if (error.response) {
            message = getMessageWithResponse(error) ?? UNKNOWN_ERROR;
            isAlert = logoutOrNavigateUser(error);
        } else {
            message = getMessageWithoutResponse(error) ?? UNKNOWN_ERROR;
        }

        return { message, isAlert };
    };

    const showCriticalAlert = (error: any) => {
        const { message, isAlert } = getCriticalMessage(error);

        isAlert &&
            setAlert({
                status: ALERT_STATUS.Critical,
                title: t(`Flash##${message}`, { defaultValue: message })
            });
    };

    const handleResponse = (res: any) => {
        if (res.data) {
            showSuccessAlert(res.data);
        } else {
            showCriticalAlert(res);
        }
    };

    return { handleResponse, getCriticalMessage };
};

export default useResponse;
